import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { mergeRegister, $getNearestNodeOfType, $findMatchingParent } from '@lexical/utils'
import {
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
  $createParagraphNode,
  $isRootOrShadowRoot,
  TextNode,
  $getRoot,
} from 'lexical'
import { $isHeadingNode } from '@lexical/rich-text'
import * as React from 'react'
import {
  INSERT_UNORDERED_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  insertList,
  $isListNode,
  ListNode,
} from '@lexical/list'
import { TOGGLE_LINK_COMMAND, $isLinkNode } from '@lexical/link'
import { $setBlocksType } from '@lexical/selection'
import { tw } from 'utils/classnames'

import { OrderedListIcon } from 'primitives/icons/rich-text-editor'
import { ListIcon } from 'primitives/icons'
import { getSelectedNode } from '../utils/get-selected-node'
import { sanitizeUrl } from '../utils/url'
import * as Popover from 'primitives/popover'
import type { EmojiClickData } from 'emoji-picker-react'
import EmojiPicker from 'emoji-picker-react'
import { Button } from '@repo/design-system-cf/components/button'
import {
  IconAddReaction,
  IconLink,
  IconFormatBold,
  IconFormatItalic,
  IconFormatUnderlined,
} from '@repo/design-system-cf/components/icons'

const blockTypeToBlockName = {
  bullet: 'Bulleted List',
  check: 'Check List',
  code: 'Code Block',
  h1: 'Heading 1',
  h2: 'Heading 2',
  h3: 'Heading 3',
  h4: 'Heading 4',
  h5: 'Heading 5',
  h6: 'Heading 6',
  number: 'Numbered List',
  paragraph: 'Normal',
  quote: 'Quote',
}

const LowPriority = 1

export function ToolbarPlugin({
  setIsLinkEditMode,
  hideEmoji,
  hideLists,
  customClassName,
  rightSlot,
}: {
  setIsLinkEditMode: (value: boolean) => void
  hideEmoji?: boolean
  hideLists?: boolean
  customIcons?: {
    bold?: React.ReactNode
    italic?: React.ReactNode
    underline?: React.ReactNode
  }
  customClassName?: string
  rightSlot?: React.ReactNode
}) {
  const [editor] = useLexicalComposerContext()
  const toolbarRef = React.useRef(null)

  const [isBold, setIsBold] = React.useState(false)
  const [isItalic, setIsItalic] = React.useState(false)
  const [isUnderline, setIsUnderline] = React.useState(false)
  const [isLink, setIsLink] = React.useState(false)
  const [blockType, setBlockType] = React.useState<keyof typeof blockTypeToBlockName>('paragraph')

  const isEditable = editor.isEditable()

  const insertLink = React.useCallback(() => {
    editor.focus()
    // oxlint-disable-next-line no-negated-condition
    if (!isLink) {
      setIsLinkEditMode(true)
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitizeUrl(''))
    } else {
      setIsLinkEditMode(false)
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null)
    }
  }, [editor, isLink, setIsLinkEditMode])

  const handleEmojiClick = React.useCallback(
    (emojiData: EmojiClickData) => {
      editor.update(() => {
        const selection = $getSelection()
        if ($isRangeSelection(selection)) {
          selection.insertText(emojiData.emoji)
        } else {
          const root = $getRoot()
          const paragraph = $createParagraphNode()
          const textNode = new TextNode(emojiData.emoji)
          paragraph.append(textNode)
          root.append(paragraph)
        }
        editor.focus()
      })
    },
    [editor],
  )

  const updateToolbar = React.useCallback(() => {
    const selection = $getSelection()
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode()
      let element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : $findMatchingParent(anchorNode, e => {
              const parent = e.getParent()
              return parent !== null && $isRootOrShadowRoot(parent)
            })

      if (element === null) {
        element = anchorNode.getTopLevelElementOrThrow()
      }

      const node = getSelectedNode(selection)

      setIsBold(selection.hasFormat('bold'))
      setIsItalic(selection.hasFormat('italic'))
      setIsUnderline(selection.hasFormat('underline'))

      let linkNode = $findMatchingParent(node, $isLinkNode)
      if ($isLinkNode(node)) {
        linkNode = node
      }
      setIsLink(Boolean(linkNode))

      if ($isListNode(element)) {
        const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode)
        const type = parentList ? parentList.getListType() : (node as ListNode).getListType()
        setBlockType(type)
      } else {
        const type = $isHeadingNode(element) ? element.getTag() : element.getType()
        if (type in blockTypeToBlockName) {
          setBlockType(type as keyof typeof blockTypeToBlockName)
        }
      }
    }
  }, [])

  const formatParagraph = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createParagraphNode())
      }
    })
  }

  const formatBulletList = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    // oxlint-disable-next-line no-negated-condition
    if (blockType !== 'bullet') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
    } else {
      formatParagraph(e)
    }
  }

  const formatNumberedList = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    // oxlint-disable-next-line no-negated-condition
    if (blockType !== 'number') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
    } else {
      formatParagraph(e)
    }
  }

  React.useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => updateToolbar())
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateToolbar()
          return false
        },
        LowPriority,
      ),
      editor.registerCommand(
        INSERT_UNORDERED_LIST_COMMAND,
        () => {
          insertList(editor, 'bullet')
          return false
        },
        LowPriority,
      ),
    )
  }, [editor, updateToolbar])

  if (!isEditable) {
    return null
  }

  return (
    <div
      ref={toolbarRef}
      onClick={e => e.stopPropagation()}
      className={tw(
        'rounded-t-2 flex h-[56px] w-full items-center gap-2 overflow-x-auto bg-[#FAFBFC] px-4',
        customClassName,
      )}
    >
      {/* LEFT: Formatting buttons */}
      <div className="flex gap-3 sm:gap-2">
        <Button
          variant="ghost"
          size="small"
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
          className={tw(isBold && 'bg-[#E6E7E8]')}
          aria-label="Bold"
          iconLeft={<IconFormatBold />}
          type="button"
        />

        <Button
          variant="ghost"
          size="small"
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
          className={tw(isItalic && 'bg-[#E6E7E8]')}
          aria-label="Italic"
          iconLeft={<IconFormatItalic />}
          type="button"
        />

        <Button
          variant="ghost"
          size="small"
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')}
          className={tw('rounded text-cf-purple-900', isUnderline && 'bg-[#E6E7E8]')}
          aria-label="Underline"
          iconLeft={<IconFormatUnderlined />}
          type="button"
        />
      </div>

      {/* Divider — removed if lists are hidden */}
      {!hideLists ? <div className="border-l h-6 border-[#1313151F]" /> : null}

      {/* Lists */}
      {!hideLists ? (
        <div className="flex gap-3 sm:gap-4">
          <button onClick={formatBulletList} aria-label="Bulleted list">
            <ListIcon />
          </button>

          <button onClick={formatNumberedList} aria-label="Numbered list">
            <OrderedListIcon />
          </button>
        </div>
      ) : null}

      <div className="border-l h-6 border-[#1313151F]" />
      <div className="flex gap-3 sm:gap-4">
        <Button
          variant="ghost"
          size="small"
          data-link-trigger
          onClick={insertLink}
          aria-label="Insert link"
          className="!px-0"
          iconLeft={<IconLink />}
          type="button"
        />
      </div>

      {!hideEmoji ? (
        <Popover.Root>
          <Popover.Trigger asChild>
            <Button
              variant="ghost"
              size="small"
              aria-label="Add Emoji"
              className="!px-0"
              iconLeft={<IconAddReaction />}
              type="button"
            />
          </Popover.Trigger>
          <Popover.Content className="z-10">
            <EmojiPicker
              onEmojiClick={handleEmojiClick}
              skinTonesDisabled
              allowExpandReactions={false}
              searchDisabled
              previewConfig={{ showPreview: false }}
            />
          </Popover.Content>
        </Popover.Root>
      ) : null}
      {rightSlot ? <div className="ml-auto flex">{rightSlot}</div> : null}
    </div>
  )
}
